import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { LockReset, Visibility, VisibilityOff } from "@mui/icons-material";
import { updatePasswordApi } from "../../../services/service";
import { toast } from "react-toastify";

function ChangePasswordPopup({ open, onComplete, pendingAuth }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const response = await updatePasswordApi({ password: newPassword });
      if (response && response.success) {
        toast.success("Password updated successfully!");
        onComplete(newPassword);
      } else {
        toast.error(response?.message || "Failed to update password");
      }
    } catch (err) {
      toast.error(err.message || "An error occurred while changing password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "28px",
          border: "1px solid rgba(15, 23, 42, 0.08)",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.15)",
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(12px)",
          p: { xs: 3, sm: 5 },
          width: { xs: "92%", sm: "480px" },
          maxWidth: "95vw",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(15, 23, 42, 0.65)",
            backdropFilter: "blur(8px)",
          },
        },
      }}
    >
      <DialogContent sx={{ p: 0, textAlign: "center" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 64,
            height: 64,
            borderRadius: "20px",
            background: "linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(21, 101, 192, 0.1) 100%)",
            color: "#1565c0",
            mb: 2.5,
          }}
        >
          <LockReset sx={{ fontSize: 32 }} />
        </Box>

        <Typography
          variant="h5"
          sx={{
            color: "#0f172a",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            mb: 1.5,
          }}
        >
          Change Password
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#64748b",
            lineHeight: 1.6,
            maxWidth: 340,
            mx: "auto",
            mb: 4,
          }}
        >
          For your security, please change your password on first login before proceeding.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" size="small">
                    {showNewPassword ? <Visibility sx={{ fontSize: "1.2rem" }} /> : <VisibilityOff sx={{ fontSize: "1.2rem" }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
              }
            }}
          />
          <TextField
            margin="dense"
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                    {showConfirmPassword ? <Visibility sx={{ fontSize: "1.2rem" }} /> : <VisibilityOff sx={{ fontSize: "1.2rem" }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
              }
            }}
          />
        </Box>

        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            height: 54,
            borderRadius: "16px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: "1rem",
            letterSpacing: "0.01em",
            background: "linear-gradient(135deg, #1565c0 0%, #0f4fa8 100%)",
            boxShadow: "0 10px 24px rgba(21,101,192,0.2)",
            "&:hover": {
              background: "linear-gradient(135deg, #0f5ab2 0%, #0b4695 100%)",
              boxShadow: "0 12px 28px rgba(21,101,192,0.28)",
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Change Password"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default ChangePasswordPopup;
